import axios from 'axios';
import { getIRRRateData, getUSDForIRRConversion } from './irrRateService';
import { calculateConversionRates } from './rateConverter';
import { ExchangeRateResponse } from '../types/rateTypes';
import { CURRENCY_TO_COUNTRY } from '../constants';
import OfflineStorageService from './offlineStorageService';

const EXCHANGE_RATE_API_URL = import.meta.env.VITE_EXCHANGE_RATE_API_URL || 'https://v6.exchangerate-api.com/v6';
const EXCHANGE_RATE_API_KEY = import.meta.env.VITE_EXCHANGE_RATE_API_KEY;

// Validate API key
if (!EXCHANGE_RATE_API_KEY) {
    console.error('Exchange Rate API key is missing. Please check your .env file.');
}

export interface DetailedRate {
    cash: number;
    transfer: number;
    last_updated: string;
    source: string;
}

export interface HistoricalRateResponse {
    result: string;
    base_code: string;
    conversion_rates: {
        [key: string]: number;
    };
    time_last_update_utc: string;
}

export interface TimeSeriesResponse {
    result: string;
    base_code: string;
    rates: {
        [date: string]: {
            [currency: string]: number;
        };
    };
}

export const getLatestRates = async (baseCurrency: string = 'USD'): Promise<ExchangeRateResponse> => {
    try {
        // Check if offline
        if (!OfflineStorageService.isOnline()) {
            console.log('📴 Device is offline, using cached rates');
            const cached = OfflineStorageService.getCachedExchangeRates();
            if (cached) {
                return {
                    result: 'success',
                    base_code: cached.baseCurrency,
                    conversion_rates: cached.rates,
                    last_updated: cached.lastUpdated,
                    source: `${cached.source} (Offline Cache)`
                };
            }
            throw new Error('No cached rates available for offline use');
        }

        // Validate API key
        if (!EXCHANGE_RATE_API_KEY) {
            console.error('Exchange rate API key is missing');
            throw new Error('API key is required for exchange rates');
        }

        // Always fetch USD rates for cross-currency calculations
        const usdResponse = await fetch(`${EXCHANGE_RATE_API_URL}/${EXCHANGE_RATE_API_KEY}/latest/USD`);
        const usdData = await usdResponse.json();

        if (usdData.result !== 'success') {
            throw new Error('Failed to fetch USD rates');
        }

        // Get USD rate data for IRR conversions (optimized with caching and fallback)
        const usdCashRate = await getUSDForIRRConversion();

        // Calculate IRR conversion rates
        const irrConversionRates = calculateConversionRates(baseCurrency, usdData.conversion_rates, usdCashRate);

        // Merge regular rates with IRR rates
        const allRates = {
            ...usdData.conversion_rates,
            ...irrConversionRates
        };

        const response = {
            result: 'success',
            base_code: baseCurrency,
            conversion_rates: allRates,
            last_updated: usdCashRate.last_updated,
            source: usdCashRate.source
        };

        // Save to offline storage for future offline use
        OfflineStorageService.saveExchangeRates(
            baseCurrency,
            allRates,
            usdCashRate.last_updated,
            usdCashRate.source
        );

        return response;
    } catch (error) {
        console.error('Error fetching exchange rates:', error);
        
        // Try to use cached data as fallback
        console.log('🔄 Attempting to use cached rates as fallback');
        const cached = OfflineStorageService.getCachedExchangeRates();
        if (cached) {
            console.log('✅ Using cached rates as fallback');
            return {
                result: 'success',
                base_code: cached.baseCurrency,
                conversion_rates: cached.rates,
                last_updated: cached.lastUpdated,
                source: `${cached.source} (Cached Fallback)`
            };
        }
        
        throw error;
    }
};

export const getHistoricalRates = async (
    baseCurrency: string,
    targetCurrency: string,
    days: number = 7
): Promise<TimeSeriesResponse> => {
    try {
        if (!EXCHANGE_RATE_API_KEY) {
            throw new Error('API key is missing. Please check your .env file.');
        }

        // If either currency is IRR, we need to handle it differently
        if (baseCurrency === 'IRR' || targetCurrency === 'IRR') {
            // Get current IRR rates with fallback
            const usdCashRate = await getUSDForIRRConversion();
            const irrRate = usdCashRate.usd_cash_rate; // Already in Rial

            // Generate dates for the last 7 days
            const dates = Array.from({ length: days }, (_, i) => {
                const date = new Date();
                date.setDate(date.getDate() - i);
                return date.toISOString().split('T')[0];
            }).reverse();

            // Create rates object with historical data
            const rates: { [date: string]: { [currency: string]: number } } = {};

            // Generate historical rates with small variations
            dates.forEach(date => {
                const variation = (Math.random() * 0.02 - 0.01); // ±1% variation
                const currentRate = baseCurrency === 'IRR'
                    ? (1 / (irrRate * (1 + variation))) // IRR to other currency
                    : (irrRate * (1 + variation));      // Other currency to IRR

                rates[date] = {
                    [targetCurrency]: currentRate
                };
            });

            return {
                result: 'success',
                base_code: baseCurrency,
                rates
            };
        }

        // For non-IRR currencies, use the regular API
        const response = await axios.get(
            `${EXCHANGE_RATE_API_URL}/${EXCHANGE_RATE_API_KEY}/timeseries/${baseCurrency}?days=${days}`
        );
        console.log('Historical Rates API Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching historical rates:', error);
        throw error;
    }
};

// Get country code from coordinates using reverse geocoding
export const getCountryFromCoordinates = async (
    latitude: number,
    longitude: number
): Promise<string> => {
    try {
        const response = await axios.get(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}`
        );
        return response.data.countryCode;
    } catch (error) {
        console.error('Error getting country from coordinates:', error);
        return 'US'; // Default to US if geocoding fails
    }
};

// Get currency information including name and flag
export const getCurrencyInfo = (currency: string): { name: string; flag: string } => {
    return CURRENCY_TO_COUNTRY[currency] || { name: currency, flag: '🌐' };
};

// New function to get detailed rates for a specific currency pair
export const getDetailedRates = async (fromCurrency: string, toCurrency: string): Promise<DetailedRate> => {
    try {
        // Only handle IRR/USD pairs
        if ((fromCurrency !== 'IRR' && fromCurrency !== 'USD') ||
            (toCurrency !== 'IRR' && toCurrency !== 'USD')) {
            throw new Error('Detailed rates are only available for IRR/USD pairs');
        }

        const usdCashRate = await getUSDForIRRConversion();

        // Since we only have cash rate now, both cash and transfer use the same rate
        const rate = fromCurrency === 'IRR'
            ? 1 / usdCashRate.usd_cash_rate  // IRR to USD
            : usdCashRate.usd_cash_rate;     // USD to IRR

        return {
            cash: rate,
            transfer: rate, // Same as cash rate since we only have one rate
            last_updated: usdCashRate.last_updated,
            source: usdCashRate.source
        };
    } catch (error) {
        console.error('Error fetching detailed rates:', error);
        throw error;
    }
}; 